import React from 'react';
import { Box, Button, Typography, Paper, Divider } from '@mui/material';
import { BugReport, ContentCopy, Refresh } from '@mui/icons-material';

interface ErrorDetails {
  message: string;
  stack?: string;
  componentStack?: string;
  timestamp: string;
  url: string;
  userAgent: string;
  errorType: 'react' | 'javascript' | 'unhandled';
  errorInfo?: any;
}

interface ErrorFallbackProps {
  error?: ErrorDetails;
  onReset?: () => void;
}

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, onReset }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopyError = async () => {
    if (!error) return;

    const errorReport = `=== FETCHDEVCONSOLE ERROR REPORT ===
Application crashed and was caught by FetchDevConsole error boundary
Captured at: ${error.timestamp}
Page URL: ${error.url}
Error Type: ${error.errorType}
User Agent: ${error.userAgent}
=======================================

ERROR DETAILS:
Message: ${error.message}

${error.stack ? `Stack Trace:\n${error.stack}\n` : ''}
${error.componentStack ? `Component Stack:\n${error.componentStack}\n` : ''}

CONSOLE LOGS:
${await getConsoleLogs()}

END OF REPORT
=======================================`;

    try {
      await navigator.clipboard.writeText(errorReport);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy error report:', err);
    }
  };

  const getConsoleLogs = async (): Promise<string> => {
    try {
      const globalWindow = window as any;
      if (globalWindow.consoleCapture?.logs?.length > 0) {
        return globalWindow.consoleCapture.logs
          .map((log: any) => `[${log.timestamp}] ${log.type}: ${log.message}`)
          .join('\n');
      }
      return 'No console logs captured';
    } catch {
      return 'Could not retrieve console logs';
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 800,
          width: '100%',
          p: 4,
          textAlign: 'center',
        }}
      >
        <BugReport color='error' sx={{ fontSize: 64, mb: 2 }} />

        <Typography variant='h4' gutterBottom color='error'>
          Application Error Detected
        </Typography>

        <Typography variant='body1' paragraph sx={{ mb: 3 }}>
          FetchDevConsole caught a critical error that prevented the application from rendering. The
          error details have been captured and can be copied for debugging.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {error && (
          <Box sx={{ textAlign: 'left', mb: 3 }}>
            <Typography variant='h6' gutterBottom>
              Error Details:
            </Typography>
            <Paper
              variant='outlined'
              sx={{
                p: 2,
                bgcolor: '#fafafa',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                overflow: 'auto',
                maxHeight: 300,
              }}
            >
              <strong>Type:</strong> {error.errorType}
              {'\n'}
              <strong>Message:</strong> {error.message}
              {'\n'}
              <strong>Time:</strong> {new Date(error.timestamp).toLocaleString()}
              {error.stack && (
                <>
                  {'\n\n'}
                  <strong>Stack Trace:</strong>
                  {'\n'}
                  {error.stack}
                </>
              )}
            </Paper>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant='contained'
            color='primary'
            startIcon={<ContentCopy />}
            onClick={handleCopyError}
            disabled={!error}
          >
            {copied ? 'Copied!' : 'Copy Full Error Report'}
          </Button>

          <Button variant='outlined' startIcon={<Refresh />} onClick={handleReload}>
            Reload Application
          </Button>

          {onReset && (
            <Button variant='outlined' color='secondary' onClick={onReset}>
              Try Reset
            </Button>
          )}
        </Box>

        <Typography variant='caption' display='block' sx={{ mt: 3, color: 'text.secondary' }}>
          Error captured by FetchDevConsole v2.0 - Enhanced error boundary system
        </Typography>
      </Paper>
    </Box>
  );
};
